/**
 * CORE-371 (P7.3) — map Agape.ChatApi `MessageDto` (delivered over the SignalR
 * hub) into the render-ready {@link ThreadMessage} the AI/Wisdom Council UI reads,
 * so switching the backend behind a flag doesn't touch a single UI component.
 *
 * The hub's JSON casing depends on its serializer policy, so every field is read
 * case-tolerantly (camelCase first, then PascalCase). Custom fields Stream kept at
 * the top of the message (ai_generated, quick_replies, …) live in ChatApi's
 * `customJson` blob; we parse and lift them here.
 */
import { getFileUrl } from '../api';
import { API_BASE_URL } from '../apiUrl';
import { normalizeChatAttachment } from './communityAttachments';
import { ReflectionOfferAction, ReplyPreview, ThreadMessage, ThreadReaction } from './chatTypes';
import { REACTION_EMOJI } from './reactions';

/** ChatApi MessageDto as seen over SignalR (loose — casing varies by hub config). */
export type ChatApiMessageDto = Record<string, any>;

function pick<T = any>(o: any, camel: string, pascal: string, dflt?: T): T {
  if (o == null) return dflt as T;
  const v = o[camel] !== undefined ? o[camel] : o[pascal];
  return (v ?? dflt) as T;
}

function parseJson(raw: any): any {
  if (!raw) return null;
  if (typeof raw !== 'string') return raw;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

/**
 * My own reactions per message id, as tracked by the channel: the slugs I have
 * set (empty when I took them all off), absent when there is no local opinion.
 * Overrides the server's `mine` flag, which the `ReactionChanged` broadcast
 * cannot carry (one payload for every member).
 */
export type MyReactionOverrides = ReadonlyMap<string, readonly string[]>;

/**
 * Мои реакции на сообщении: что сделал этот клиент, а без того — серверный
 * `mine`. Все, а не первая: CORE-1479 (возврат QA 23.10) — сервер хранит
 * реакции по типу, и вторая своя (прежняя `love` рядом с новой) терялась.
 */
export function myReactionsOf(dto: ChatApiMessageDto, overrides?: MyReactionOverrides): string[] {
  const local = overrides?.get(String(pick(dto, 'id', 'Id', '')));
  if (local !== undefined) return [...local];
  const raw: any[] = pick(dto, 'reactions', 'Reactions', []) || [];
  return raw
    .filter((r) => pick(r, 'mine', 'Mine', false))
    .map((r) => String(pick(r, 'type', 'Type', '')))
    .filter(Boolean);
}

/** The `ReactionChanged` frame of the ChatApi hub beyond the counts: who did what, when the server names it. */
export interface ChatApiReactionChangedFrame {
  userId: string;
  type: string;
  /** null — the frame does not say whether the reaction was added or removed. */
  added: boolean | null;
}

export function parseChatApiReactionChanged(p: any): ChatApiReactionChangedFrame {
  const addedRaw = pick<unknown>(p, 'added', 'Added', undefined);
  return {
    userId: String(pick(p, 'userId', 'UserId', '') ?? ''),
    type: String(pick(p, 'type', 'Type', '') ?? ''),
    added: addedRaw === undefined || addedRaw === null ? null : !!addedRaw,
  };
}

/**
 * CORE-1685 (QA george 29.10.2026, FAIL on dev 610d649) — the `mine` flag inside a
 * `ReactionChanged` broadcast is the actor's, not the reader's: B received A's reaction with
 * `mine: true`, drew it as its own and its first tap sent RemoveReaction. The flag in the stored
 * list is therefore never taken from the frame. It is what this client already knew — its own
 * record (`myReactions` override) or the previously stored list — corrected by the frame only
 * when the frame names this client as the actor (the live chat does the same,
 * `applyReactionChanged`). Returns the stamped list and the resulting own slugs.
 */
export function reactionsAfterChanged(
  incoming: any[] | undefined,
  prev: any[] | undefined,
  myOverride: readonly string[] | undefined,
  frame: ChatApiReactionChangedFrame,
  myId: string | undefined,
): { reactions: any[]; mine: string[]; aboutMe: boolean } {
  const mine = new Set<string>(
    myOverride !== undefined
      ? myOverride
      : (prev ?? []).filter((r) => pick(r, 'mine', 'Mine', false)).map((r) => String(pick(r, 'type', 'Type', ''))).filter(Boolean),
  );
  const aboutMe = !!myId && !!frame.userId && frame.userId === myId && !!frame.type && frame.added !== null;
  if (aboutMe) {
    if (frame.added) mine.add(frame.type);
    else mine.delete(frame.type);
  }
  const reactions = (incoming ?? []).map((r) => {
    const { Mine: _pascal, ...rest } = r ?? {};
    return { ...rest, mine: mine.has(String(pick(r, 'type', 'Type', ''))) };
  });
  return { reactions, mine: [...mine], aboutMe };
}

export function mapChatApiMessage(
  dto: ChatApiMessageDto,
  myId?: string,
  othersLastReadId = 0,
  myReactions?: MyReactionOverrides,
  /**
   * CORE-1791 — автор сообщения по id среди загруженных: для цитаты через
   * `parentMessageId`, когда сервер не прислал `quotedAuthorUserId`.
   */
  authorOf?: (messageId: string) => string | undefined,
): ThreadMessage {
  const id = String(pick(dto, 'id', 'Id', ''));
  const author = pick<Record<string, any>>(dto, 'author', 'Author', {}) || {};
  const authorId = String(pick(author, 'userId', 'UserId', ''));
  const custom = parseJson(pick(dto, 'customJson', 'CustomJson', null)) || {};
  const attachmentsRaw = parseJson(pick(dto, 'attachmentsJson', 'AttachmentsJson', null));
  // CORE-1682 — server-stored uploads come as `{ id, type, fileName, url }`
  // with a signed, API-relative link; shape them for `AttachmentView`.
  const attachments = Array.isArray(attachmentsRaw)
    ? attachmentsRaw.map((a) => normalizeChatAttachment(a, API_BASE_URL))
    : [];
  const createdAt = new Date(pick(dto, 'createdAtUtc', 'CreatedAtUtc', Date.now()));

  const reactionsRaw: any[] = pick(dto, 'reactions', 'Reactions', []) || [];
  const reactions: ThreadReaction[] = reactionsRaw
    .filter((r) => (pick(r, 'count', 'Count', 0) as number) > 0)
    .map((r) => ({
      type: String(pick(r, 'type', 'Type', '')),
      emoji: REACTION_EMOJI[pick(r, 'type', 'Type', '')] || '❤️',
      count: pick(r, 'count', 'Count', 0) as number,
    }));
  // What this client last did wins over the broadcast: `ReactionChanged` is sent
  // to every member at once and so has no per-user `mine`, and it replaces the
  // stored list — leaving the flag lost right after reacting. Without the
  // override, tapping my own emoji again read as «no reaction yet» and added it
  // a second time instead of removing it.
  const mine = myReactionsOf(dto, myReactions);

  const quickRepliesRaw = custom.quick_replies ?? custom.quickReplies;
  const quickReplies = Array.isArray(quickRepliesRaw)
    ? quickRepliesRaw.filter((q: any) => typeof q === 'string')
    : [];

  // CORE-396 — the ChatApi backend attaches the same AI affordances Stream did
  // (highlights + recommendation cards, XOR with quick replies). Lift them so
  // the agnostic thread renders full parity, not just plain text.
  const highlightsRaw = custom.agape_highlights ?? custom.agapeHighlights;
  const highlights = Array.isArray(highlightsRaw)
    ? highlightsRaw.filter((h: any) => h && typeof h.text === 'string' && typeof h.reply === 'string')
    : undefined;
  const cardsRaw = custom.agape_cards ?? custom.agapeCards;
  const cards = Array.isArray(cardsRaw) ? cardsRaw : undefined;
  const reflectionOffer = parseReflectionOffer(custom.reflection_offer ?? custom.reflectionOffer);

  const numericId = Number(id);
  const clientMessageIdRaw = pick(dto, 'clientMessageId', 'ClientMessageId', null);

  // CORE-356 (B4) — quoted-reply preview + pinned flag now on the MessageDto.
  const parentId = pick(dto, 'parentMessageId', 'ParentMessageId', null);
  const quotedText = pick(dto, 'quotedText', 'QuotedText', null) as string | null;
  const quotedAuthor = pick(dto, 'quotedAuthorName', 'QuotedAuthorName', null) as string | null;
  // CORE-1791 (возврат QA 23.10) — ответ в личном диалоге хранится через
  // `parentMessageId` (цитата — `quoted`, а не `replyTo`), и у неё не было
  // автора: своя цитата подписывалась своим именем, а не «Вы». Автор — из поля
  // сервера, а пока его нет — у того же сообщения в ленте.
  const quotedAuthorRaw = pick(dto, 'quotedAuthorUserId', 'QuotedAuthorUserId', null);
  const quotedAuthorId = quotedAuthorRaw != null && quotedAuthorRaw !== ''
    ? String(quotedAuthorRaw)
    : (parentId != null ? authorOf?.(String(parentId)) : undefined);

  // CORE-1683 — the answered message, separate from the discussion parent.
  // Snapshot fields: once the original is deleted the server blanks `text` and
  // `attachmentsJson` and raises `isDeleted`; the author stays.
  const replyToRaw = pick<Record<string, any> | null>(dto, 'replyTo', 'ReplyTo', null);
  const replyToId = replyToRaw ? pick(replyToRaw, 'messageId', 'MessageId', null) : null;
  let replyTo: ReplyPreview | null = null;
  if (replyToRaw && replyToId != null) {
    const replyAttachmentsRaw = parseJson(pick(replyToRaw, 'attachmentsJson', 'AttachmentsJson', null));
    const replyAttachments = Array.isArray(replyAttachmentsRaw)
      ? replyAttachmentsRaw.map((a) => normalizeChatAttachment(a, API_BASE_URL))
      : [];
    const replyAuthorId = pick(replyToRaw, 'authorUserId', 'AuthorUserId', null);
    // CORE-1749 — the selected phrase, when the reply is about one.
    const replyFragment = (pick(replyToRaw, 'fragment', 'Fragment', '') as string) || '';
    replyTo = {
      id: String(replyToId),
      senderId: replyAuthorId == null ? undefined : String(replyAuthorId),
      senderName: (pick(replyToRaw, 'authorName', 'AuthorName', '') as string) || '',
      // Тем же сравнением, что `isMine` у самого сообщения ниже; поле только когда
      // «да» — чужие цитаты остаются той же формы, что и раньше.
      ...(!!myId && replyAuthorId != null && String(replyAuthorId) === myId ? { isMine: true } : {}),
      text: (pick(replyToRaw, 'text', 'Text', '') as string) || '',
      attachments: replyAttachments,
      deleted: !!pick(replyToRaw, 'isDeleted', 'IsDeleted', false),
      ...(replyFragment ? { fragment: replyFragment } : {}),
    };
  }

  return {
    id,
    text: (pick(dto, 'text', 'Text', '') as string) || '',
    isMine: !!myId && authorId === myId,
    createdAt,
    read: !!othersLastReadId && numericId > 0 && numericId <= othersLastReadId,
    senderName: (pick(author, 'name', 'Name', '') as string) || '',
    senderAvatar: getFileUrl(pick(author, 'avatarUrl', 'AvatarUrl', undefined)) || null,
    // CORE-642 — message rows draw the author at ~30 px; carrying the preview
    // means a busy thread stops fetching one full-size upload per author.
    senderAvatarThumb: getFileUrl(pick(author, 'avatarThumbUrl', 'AvatarThumbUrl', undefined)) || null,
    senderId: authorId || undefined,
    attachments,
    reactions,
    myReactions: mine,
    pinned: !!pick(dto, 'pinned', 'Pinned', false),
    // CORE-1791 — только у своих: чужой ключ композеру ни о чём не говорит.
    ...(!!myId && authorId === myId && clientMessageIdRaw ? { clientMessageId: String(clientMessageIdRaw) } : {}),
    quoted: parentId
      ? {
        id: String(parentId),
        text: quotedText || '',
        senderName: quotedAuthor || '',
        ...(quotedAuthorId ? { senderId: quotedAuthorId } : {}),
        // Тем же сравнением, что `isMine` у самого сообщения; поле только когда «да».
        ...(!!myId && quotedAuthorId === myId ? { isMine: true } : {}),
      }
      : null,
    replyTo,
    aiGenerated: !!custom.ai_generated || authorId.startsWith('ai_'),
    quickReplies,
    highlights,
    cards,
    reflectionOffer,
    subscriptionRequired: custom.agapella_subscription_required === true,
    // CORE-356 — carry the raw dto (plus the PARSED custom bag) so circle event
    // cards work on the ChatApi path too: isEventCardMessage / extractEvent read
    // `raw.custom?.eventId` etc., and ChatApi keeps those under a JSON string.
    // Mirrors what the Stream mapper does (which carries the live message).
    raw: { ...(dto as any), custom },
  };
}

/**
 * CORE-1751 — поле кнопки рефлексии из `customJson`. Без настоящего числового
 * id завершения кнопки нет: открыть окно без привязки значило бы записать
 * рефлексию «о Live-сессии», не связанную ни с какой сессией.
 */
export function parseReflectionOffer(raw: unknown): ReflectionOfferAction | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const offer = raw as Record<string, unknown>;
  const completionId = offer.completion_id ?? offer.completionId;
  if (typeof completionId !== 'number' || !Number.isInteger(completionId) || completionId <= 0) return undefined;
  const title = typeof offer.title === 'string' && offer.title.trim() ? offer.title.trim() : null;
  return { completionId, title };
}

/**
 * CORE-1681 (QA 17.10) — удалённое сообщение сервер не вычёркивает из истории:
 * строка остаётся с `isDeleted`, чтобы ответы на неё знали, что оригинала
 * больше нет. На экран такое не попадает — но и «найденным» для перехода по
 * ссылке считаться не должно.
 */
export function isDeletedChatApiMessage(dto: ChatApiMessageDto | undefined): boolean {
  return !!dto && !!pick(dto, 'isDeleted', 'IsDeleted', false);
}

export function mapChatApiMessages(
  dtos: ChatApiMessageDto[],
  myId?: string,
  othersLastReadId = 0,
  myReactions?: MyReactionOverrides,
): ThreadMessage[] {
  // CORE-1791 — автор каждого загруженного сообщения, удалённые тоже: цитата
  // остаётся, когда оригинал удалён, а автор у него прежний.
  const authors = new Map<string, string>();
  for (const d of dtos) {
    const id = String(pick(d, 'id', 'Id', ''));
    const author = String(pick(pick<Record<string, any>>(d, 'author', 'Author', {}) || {}, 'userId', 'UserId', ''));
    if (id && author) authors.set(id, author);
  }
  const authorOf = (messageId: string) => authors.get(messageId);
  return dtos
    .filter((d) => !isDeletedChatApiMessage(d))
    .map((d) => mapChatApiMessage(d, myId, othersLastReadId, myReactions, authorOf))
    // Time first, then the server id as a tie-breaker. Timestamps collide more
    // often than they look: two messages sent in the same burst, and above all a
    // streaming AI reply, can share a millisecond. With time as the only key the
    // comparator was not a total order, so equal-time messages could swap places
    // between renders. The id is monotonic server-side, so it settles them.
    .sort((a, b) => {
      const byTime = a.createdAt.getTime() - b.createdAt.getTime();
      if (byTime !== 0) return byTime;
      const ai = Number(a.id);
      const bi = Number(b.id);
      // Non-numeric ids (optimistic locals) sort by string, still deterministic.
      if (Number.isFinite(ai) && Number.isFinite(bi)) return ai - bi;
      return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
    });
}
